<template>
    <div>
        <div class="container-fluid">
            <div class="row widget-grid" style="padding-top: 30px;">
                <div v-if="pageError" class="col-12">
                    <div class="alert alert-warning py-2" role="alert">{{ pageError }}</div>
                </div>

                <div class="col-12 mb-3">
                    <div class="validations-hero">
                        <div>
                            <h3 class="mb-1">Validaciones</h3>
                            <span class="hero-subtitle">CFDIs con situaciones que pudieran ser incorrectas</span>
                        </div>
                    </div>
                </div>

                <div class="col-12 mb-3">
                    <div class="section-accent section-accent-income mb-2">
                        <h5 class="mb-0">CFDIs Emitidos</h5>
                    </div>
                    <div class="row gx-3">
                        <div v-for="card in issuedValidationCards" :key="card.key" class="col-12 col-md-6 col-xl-4 mb-0">
                            <AnomalyMetricCard
                                :title="card.title"
                                :subtitle="card.subtitle"
                                :value="card.period"
                                :label="periodLabel"
                                tone="income"
                                @click="openAnomalyDrilldown(card)"
                            />
                        </div>
                    </div>
                </div>

                <div class="col-12">
                    <div class="section-accent section-accent-expense mb-2">
                        <h5 class="mb-0">CFDIs Recibidos</h5>
                    </div>
                    <div class="row gx-3">
                        <div v-for="card in receivedValidationCards" :key="card.key" class="col-12 col-md-6 col-xl-4 mb-0">
                            <AnomalyMetricCard
                                :title="card.title"
                                :subtitle="card.subtitle"
                                :value="card.period"
                                :label="periodLabel"
                                tone="expense"
                                @click="openAnomalyDrilldown(card)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="drilldownModal.visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
            <div class="modal-dialog modal-xl" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ drilldownModal.title }}</h5>
                        <button type="button" class="btn-close" aria-label="Close" @click="closeDrilldownModal"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="drilldownModal.loading" class="text-center py-4">Consultando detalle...</div>
                        <div v-else-if="drilldownModal.error" class="alert alert-danger mb-0" role="alert">{{ drilldownModal.error }}</div>
                        <div v-else class="table-responsive">
                            <table class="table drilldown-table" :class="drilldownTableClass">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Método</th>
                                        <th>Folio / UUID</th>
                                        <th>{{ drilldownModal.type === 'incomes' ? 'Receptor' : 'Emisor' }}</th>
                                        <th class="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!drilldownModal.rows.length">
                                        <td colspan="5" class="text-center">No se encontraron registros para este periodo.</td>
                                    </tr>
                                    <tr v-for="(row, index) in drilldownModal.rows" :key="index">
                                        <td>{{ formatDate(row.fecha) }}</td>
                                        <td>
                                            <span :class="['method-badge', getMethodBadgeClass(row.metodo_pago)]">
                                                {{ row.metodo_pago || '-' }}
                                            </span>
                                        </td>
                                        <td>
                                            {{ row.folio_cfdi || '-' }}
                                            <br />
                                            <span>{{ formatUuid(row.uuid_cfdi) || '-' }}</span>
                                        </td>
                                        <td>{{ row.counterpart_rfc || '-' }}<br />{{ row.counterpart_name || '-' }}</td>
                                        <td class="text-end amount-cell" :class="amountAccentClass">{{ formatAmount(row.monto_calculo) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot v-if="drilldownModal.rows.length">
                                    <tr>
                                        <th colspan="4" class="text-end">Total</th>
                                        <th class="text-end amount-cell" :class="amountAccentClass">{{ formatAmount(drilldownTotal) }}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" @click="closeDrilldownModal">Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="drilldownModal.visible" class="modal-backdrop fade show"></div>
    </div>
</template>

<script>
import moment from "moment";
import ws from "@/services/dashboard";
import AnomalyMetricCard from "@/components/dashboard/AnomalyMetricCard.vue";
import { GLOBAL_PERIOD_EVENT, getGlobalPeriod } from "@/services/globalPeriod";
import { loadingShow, loadingHide } from "@/plugins/loadingControls";

export default {
    components: {
        AnomalyMetricCard,
    },
    data() {
        return {
            rfc: "",
            period: "",
            pageError: "",
            anomalies: {
                issued: {
                    incomes_pue_forma_99: { period: 0, year: 0 },
                    incomes_pue_with_related_payments: { period: 0, year: 0 },
                    expenses_without_related_cfdi: { period: 0, year: 0 },
                },
                received: {
                    incomes_pue_forma_99: { period: 0, year: 0 },
                    incomes_pue_with_related_payments: { period: 0, year: 0 },
                    expenses_without_related_cfdi: { period: 0, year: 0 },
                    regimen_fiscal_invalid: { period: 0, year: 0 },
                },
            },
            monthLabelsLong: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
            drilldownModal: {
                visible: false,
                loading: false,
                error: "",
                title: "",
                type: "",
                rows: [],
            },
        };
    },
    computed: {
        periodLabel() {
            const period = String(this.period || "");

            if (/^\d{4}-\d{2}$/.test(period)) {
                const [year, month] = period.split("-");
                const monthIndex = Number(month) - 1;
                const monthName = this.monthLabelsLong?.[monthIndex] || "";
                return `${monthName} ${year}`.trim();
            }

            if (/^\d{4}$/.test(period)) {
                return `${period} (año completo)`;
            }

            return "Periodo";
        },
        issuedValidationCards() {
            const issued = this.anomalies?.issued || {};

            return [
                {
                    key: "issued-incomes-pue-forma-99",
                    anomaly: "incomes_pue_forma_99",
                    nature: "issued",
                    title: "Ingresos PUE",
                    subtitle: "con forma de pago 99",
                    period: issued?.incomes_pue_forma_99?.period || 0,
                },
                {
                    key: "issued-incomes-pue-with-payments",
                    anomaly: "incomes_pue_with_related_payments",
                    nature: "issued",
                    title: "Ingresos PUE",
                    subtitle: "con CFDIs de pago relacionados",
                    period: issued?.incomes_pue_with_related_payments?.period || 0,
                },
                {
                    key: "issued-expenses-without-rel",
                    anomaly: "expenses_without_related_cfdi",
                    nature: "issued",
                    title: "Egresos",
                    subtitle: "sin CFDI relacionado",
                    period: issued?.expenses_without_related_cfdi?.period || 0,
                },
            ];
        },
        receivedValidationCards() {
            const received = this.anomalies?.received || {};

            return [
                {
                    key: "received-incomes-pue-forma-99",
                    anomaly: "incomes_pue_forma_99",
                    nature: "received",
                    title: "Ingresos PUE",
                    subtitle: "con forma de pago 99",
                    period: received?.incomes_pue_forma_99?.period || 0,
                },
                {
                    key: "received-incomes-pue-with-payments",
                    anomaly: "incomes_pue_with_related_payments",
                    nature: "received",
                    title: "Ingresos PUE",
                    subtitle: "con CFDIs de pago relacionados",
                    period: received?.incomes_pue_with_related_payments?.period || 0,
                },
                {
                    key: "received-expenses-without-rel",
                    anomaly: "expenses_without_related_cfdi",
                    nature: "received",
                    title: "Egresos",
                    subtitle: "sin CFDI relacionado",
                    period: received?.expenses_without_related_cfdi?.period || 0,
                },
                {
                    key: "received-regimen-fiscal-invalid",
                    anomaly: "regimen_fiscal_invalid",
                    nature: "received",
                    title: "Régimen Fiscal",
                    subtitle: "incorrecto o no válido",
                    period: received?.regimen_fiscal_invalid?.period || 0,
                },
            ];
        },
        drilldownTotal() {
            return (this.drilldownModal.rows || []).reduce((sum, row) => {
                return sum + Number(row?.monto_calculo || 0);
            }, 0);
        },
        amountAccentClass() {
            return this.drilldownModal.type === "expenses" ? "amount-expense" : "amount-income";
        },
        drilldownTableClass() {
            return this.drilldownModal.type === "expenses" ? "drilldown-expense" : "drilldown-income";
        },
    },
    async mounted() {
        this.rfc = localStorage.getItem("rfc");
        this.period = getGlobalPeriod();

        window.addEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);

        await this.refreshItems();
    },
    beforeDestroy() {
        window.removeEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
    },
    methods: {
        handleGlobalPeriodChanged(event) {
            const period = String(event?.detail?.period || "").trim();

            if (!period || period === this.period) {
                return;
            }

            this.period = period;
            this.refreshItems();
        },
        async refreshItems() {
            this.pageError = "";

            loadingShow({ text: "Cargando validaciones..." });

            try {
                const filters = {
                    rfc: this.rfc,
                    period: this.period,
                };

                const response = await ws.getDashboardAnomalies(this.rfc, filters, { skipLoading: true });

                if (response && response.type === "success") {
                    this.anomalies = {
                        issued: {
                            incomes_pue_forma_99: response?.data?.issued?.incomes_pue_forma_99 || { period: 0, year: 0 },
                            incomes_pue_with_related_payments: response?.data?.issued?.incomes_pue_with_related_payments || { period: 0, year: 0 },
                            expenses_without_related_cfdi: response?.data?.issued?.expenses_without_related_cfdi || { period: 0, year: 0 },
                        },
                        received: {
                            incomes_pue_forma_99: response?.data?.received?.incomes_pue_forma_99 || { period: 0, year: 0 },
                            incomes_pue_with_related_payments: response?.data?.received?.incomes_pue_with_related_payments || { period: 0, year: 0 },
                            expenses_without_related_cfdi: response?.data?.received?.expenses_without_related_cfdi || { period: 0, year: 0 },
                            regimen_fiscal_invalid: response?.data?.received?.regimen_fiscal_invalid || { period: 0, year: 0 },
                        },
                    };
                } else {
                    this.pageError = "No fue posible cargar las validaciones. Intenta nuevamente.";
                }
            } catch (error) {
                this.pageError = "No fue posible cargar las validaciones. Intenta nuevamente.";
            } finally {
                loadingHide();
            }
        },
        closeDrilldownModal() {
            this.drilldownModal.visible = false;
            this.drilldownModal.loading = false;
            this.drilldownModal.error = "";
            this.drilldownModal.title = "";
            this.drilldownModal.type = "";
            this.drilldownModal.rows = [];
        },
        formatAmount(value) {
            const number = Number(value || 0);
            return new Intl.NumberFormat("es-MX", {
                style: "currency",
                currency: "MXN",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(number);
        },
        formatDate(value) {
            if (!value) return "-";
            return moment(value).format("DD/MM/YYYY");
        },
        formatUuid(value) {
            if (!value) return "";
            return String(value).toUpperCase();
        },
        getMethodBadgeClass(method) {
            if (method === "PUE") return "method-primary";
            if (method === "PPD") return "method-secondary";
            return "method-neutral";
        },
        async openAnomalyDrilldown(card) {
            const side = card?.nature === "received" ? "expenses" : "incomes";
            const anomalyTitles = {
                incomes_pue_forma_99: "CFDIs de Ingresos PUE con forma de pago 99",
                incomes_pue_with_related_payments: "CFDIs de Ingresos PUE con CFDIs de pago relacionados",
                expenses_without_related_cfdi: "CFDIs de Egresos sin CFDI relacionado",
                regimen_fiscal_invalid: "CFDIs Recibidos con Regimen Fiscal incorrecto o no valido",
            };
            const anomalyLabel = anomalyTitles[card?.anomaly] || `${card?.title || "Validación"} ${card?.subtitle || ""}`.trim();

            this.drilldownModal.visible = true;
            this.drilldownModal.loading = true;
            this.drilldownModal.error = "";
            this.drilldownModal.rows = [];
            this.drilldownModal.type = side;
            this.drilldownModal.title = `${anomalyLabel} - ${this.periodLabel}`;

            const detailFilters = {
                rfc: this.rfc,
                period: this.period,
                nature: card?.nature,
                anomaly: card?.anomaly,
            };

            try {
                const response = await ws.getDashboardAnomalyDetail(this.rfc, detailFilters, { skipLoading: true });

                if (response && response.type === "success") {
                    this.drilldownModal.rows = response.data || [];
                } else {
                    this.drilldownModal.error = "No se pudo consultar el detalle de la validación.";
                }
            } catch (error) {
                this.drilldownModal.error = "No se pudo consultar el detalle de la validación.";
            } finally {
                this.drilldownModal.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.validations-hero {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
    background:
        linear-gradient(135deg, rgba(115, 102, 255, 0.14), rgba(247, 49, 100, 0.08));
    border: 1px solid rgba(115, 102, 255, 0.24);
}

.hero-subtitle {
    color: rgba(82, 82, 108, 0.92);
}

.section-accent {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border-radius: 8px;
    border-left: 4px solid transparent;
    background: rgba(0, 0, 0, 0.02);
}

.section-accent-income {
    border-left-color: rgb(115, 102, 255);
    background: rgba(115, 102, 255, 0.08);
}

.section-accent-expense {
    border-left-color: rgb(247, 49, 100);
    background: rgba(247, 49, 100, 0.08);
}

.method-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.method-primary {
    background: rgba(115, 102, 255, 0.15);
    color: rgb(115, 102, 255);
}

.method-secondary {
    background: rgba(247, 49, 100, 0.15);
    color: rgb(247, 49, 100);
}

.method-neutral {
    background: rgba(82, 82, 108, 0.12);
    color: rgba(82, 82, 108, 0.9);
}

.amount-income {
    color: rgb(115, 102, 255);
    font-weight: 600;
}

.amount-expense {
    color: rgb(247, 49, 100);
    font-weight: 600;
}

.drilldown-table tbody tr,
.drilldown-table tbody tr:nth-of-type(odd),
.drilldown-table tbody tr:nth-of-type(even) {
    background-color: transparent;
}

.drilldown-table.drilldown-income tbody tr {
    background-color: rgba(115, 102, 255, 0.06);
}

.drilldown-table.drilldown-expense tbody tr {
    background-color: rgba(247, 49, 100, 0.06);
}

.modal-title {
    white-space: pre-line;
}

@media (max-width: 576px) {
    .validations-hero {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
